/**
 * The help content for every page.
 *
 * # Why it lives here and not beside each page
 *
 * A help text that drifts from the screen it describes is worse than none: the
 * reader trusts it and acts on something that is no longer true. Keeping every
 * page's help in one typed table makes the drift visible — a page id with no
 * entry fails a test rather than quietly rendering a button that explains
 * nothing, and a reviewer changing a state or a filter sees every description
 * of it in one file.
 *
 * # What every entry must answer
 *
 * The four questions an operator actually has, in the order they have them:
 * what am I looking at, what do the controls do, how do I read the result, and
 * what should I do about it. The last one is the one help texts usually omit
 * and the only one anybody opened the panel for.
 *
 * # The invariants are stated, not assumed
 *
 * Every page that shows a state or a number repeats the two rules this product
 * is built on, in the reader's own terms: a check that could not run is neither
 * a pass nor a failure, and a score without its coverage is not a measurement.
 * They are repeated per page deliberately — somebody reading the Findings page
 * has not necessarily read the Overview.
 */

/** One row of a parameter or result table. */
export interface HelpTerm {
  /** The control or value as it appears on screen. */
  term: string;
  /** What it does, or what it means. */
  meaning: string;
  /**
   * The consequence of getting it wrong, where there is one worth stating.
   * Rendered emphasised, because it is what stops a plausible misreading.
   */
  caution?: string;
}

export interface HelpSection {
  title: string;
  /** Prose paragraphs. */
  body?: string[];
  /** A term table. */
  terms?: HelpTerm[];
}

export interface PageHelp {
  /** The page title, echoed so the panel is self-identifying. */
  title: string;
  /** One sentence: what this page answers. */
  purpose: string;
  sections: HelpSection[];
}

/** The two rules, in the words each page repeats. */
const NOT_COLLECTED: HelpTerm = {
  term: "Not collected",
  meaning:
    "The check could not run — a missing permission, an unreachable connector, a module " +
    "switched off — and it carries the reason. It is excluded from the score and it drives " +
    "coverage down.",
  caution:
    "It is not a pass and not a failure. Its affected count reads “not counted”, never 0: " +
    "nothing was measured, which is a different fact from measuring zero.",
};

const NOT_APPLICABLE: HelpTerm = {
  term: "Not applicable",
  meaning:
    "The control does not apply to this tenant — there is nothing of that kind here to " +
    "judge. It leaves both the score and the coverage denominator.",
  caution:
    "Different from “not collected”: that one means nobody looked, this one means there " +
    "was nothing to look at.",
};

const ACCEPTED: HelpTerm = {
  term: "Accepted",
  meaning:
    "A failing check somebody accepted as a known risk, with an owner, a reason and an " +
    "expiry date. It counts as accepted in the score, never as a pass.",
  caution:
    "The acceptance stops applying the moment it expires, and the stored result still says " +
    "“fail” — the history is not rewritten.",
};

const COVERAGE_RULE =
  "A score is never shown without its coverage. 92 % at 41 % coverage is a completely " +
  "different claim from 92 % at 98 %: the first says most of the tenant was never " +
  "measured. Where nothing could be measured the score reads “Not measured” — never 0 %, " +
  "because a tenant where no check could run is not a tenant that failed everything.";

/** D32: the two scores every bucket carries, one sentence each. */
const TWO_SCORES =
  "The large number is pass vs all: the share of every applicable check (or control) that " +
  "passed — one that could not run is not a pass, so it counts against it, and the gauge " +
  "draws it hatched, never as a fail. The line beneath it is pass vs executed: the share " +
  "of the checks that produced a verdict which passed.";

/*
 * The four blocks of the Tenant connection page. They were four pages once,
 * each with its own help entry; the page now has one entry made of the four,
 * every section titled with the block it explains.
 */
const CREDENTIALS_HELP: PageHelp = {
  title: "Credentials",
  purpose: "The application registration this deployment reads the tenant with.",
  sections: [
    {
      title: "What to do here",
      body: [
        "Register the tenant's application, then run Verify. Verify probes what the " +
          "credential can actually do and stores the result — it is what every capability " +
          "gate then reads, so a scan never guesses.",
        "A secret and a certificate both work for every connector, including Exchange " +
          "Online PowerShell. Certificate authentication is classified as legacy " +
          "authentication, so a Conditional Access policy blocking legacy auth — a control " +
          "this product recommends — will block it, and that has its own reason code.",
      ],
    },
  ],
};

const TENANT_INIT_HELP: PageHelp = {
  title: "Tenant init",
  purpose:
    "Set up this tenant — the scanner app, its permissions and its read-only roles — with a Global Administrator's interactive sign-in.",
  sections: [
    {
      title: "The setup app",
      body: [
        "The sign-in goes through this tenant's own setup app: a single-tenant single-page application that a Global Administrator of this tenant creates once in the Microsoft Entra admin center (Accounts in this organizational directory only). There is no shared or central app. A single-tenant deployment has exactly one setup app; with several console tenants, each has its own, in its own directory, entered on its own Tenant init page.",
        "Enter the setup app's Directory (tenant) ID and Application (client) ID under Setup app, and register the redirect URI shown there on the app. The sign-in authority is login.microsoftonline.com/<Directory (tenant) ID>; a sign-in from any other directory is refused.",
        "By default the setup app is kept after the run. Choose “Delete the setup app registration after a successful run” on the plan to remove it; a later rerun then needs a new setup app.",
      ],
    },
    {
      title: "Who does what",
      body: [
        "A console admin opens this page. A Global Administrator of this tenant signs in to Microsoft in the popup only, with MFA. Keep the console session your own: making that Global Administrator a console admin gives them every tenant of this deployment, because operators are not per tenant.",
        "On Microsoft's consent prompt, leave “Consent on behalf of your organization” unticked. A self-only grant lets this one administrator run the wizard and nobody else in this tenant use the setup app.",
        "The one-time setup is not performed by sp-ms365's server. The wizard runs in this browser and makes the setup changes with the Global Administrator's own interactive, MFA-verified sign-in. sp-ms365's server never receives that sign-in's tokens, and holds no write scope afterwards. The tokens live only in this tab's memory and are cleared at the end, on leaving the page, and after 20 idle minutes.",
      ],
    },
    {
      title: "Checked before anything is written",
      terms: [
        {
          term: "Directory",
          meaning:
            "The sign-in's directory (tid) must be the setup app's directory, which is the one this console tenant scans. The first run binds an unbound console tenant to it, after you confirm the directory by name.",
        },
        {
          term: "Global Administrator",
          meaning:
            "An ACTIVE Global Administrator assignment, read from Microsoft Graph — directly or through a group.",
          caution: "A PIM-eligible assignment is not active. Activate it in PIM, then sign in again.",
        },
        {
          term: "MFA",
          meaning:
            "Judged by Setup app → Sign-in MFA gate. Follow Conditional Access (the default): the console relies on this tenant's Conditional Access — the sign-in is accepted when Entra issues the token, and the methods used (amr) and any satisfied authentication context (acrs) are recorded and shown as neutral information. amr is recorded evidence, not a gate. Require MFA evidence: amr must contain mfa, regardless of Conditional Access, or the run is refused. Run and record: a sign-in without MFA runs with a red “MFA not evidenced for this run” banner on the record.",
          caution:
            "Add the optional ID-token claim amr to the setup app so the audit is complete: without it Follow Conditional Access records the methods as not issued by Entra, and Require MFA evidence and Run and record refuse the run. To force step-up for this app, configure an authentication context whose policy requires MFA.",
        },
        {
          term: "Authentication context",
          meaning:
            "Shown when one is configured: whether the token's acrs lists it, or unknown when the ID token carries no acrs claim. It is requested in every mode, so Conditional Access can demand step-up, and the outcome is recorded. It never decides the outcome on its own — Microsoft issues a context no policy protects without asking for anything.",
        },
      ],
    },
    {
      title: "Reading the run",
      terms: [
        { term: "Created now", meaning: "Written by this run AND confirmed by a read-back." },
        { term: "Already there", meaning: "Found in place by a read; nothing was written." },
        {
          term: "Manual — not done",
          meaning:
            "No documented browser-callable API exists (Exchange and Purview role groups, the sidecar certificate), or the browser was blocked before sending. The exact instruction is shown, filled in with the real ids.",
          caution: "It is never done. The capability probe is the only evidence the manual step was carried out.",
        },
        {
          term: "Unconfirmed",
          meaning:
            "A write may have gone through, but neither its answer nor a read-back could be read. Check it by hand.",
          caution: "Never a success: the run cannot complete with an unconfirmed step.",
        },
        { term: "Blocked", meaning: "Not attempted, because a step it depends on did not succeed." },
        {
          term: "Verify",
          meaning:
            "The capability probe, run by the server with the new credential. It shows what the scanner can actually do, beside what Microsoft answered to each step.",
          caution:
            "A gap right after the run is not final: a role grant shows on the next token, Azure RBAC takes up to 10 minutes, Exchange 30 minutes to 2 hours. Re-verify later.",
        },
      ],
    },
    {
      title: "Reruns are dangerous and blocked",
      body: [
        "Once a run has completed, another one is refused. A rerun creates a NEW client secret and may re-assign roles. It is allowed once only when the Dangerous switch (tenant_init_rerun_allowed) is on, and only after you type the confirmation phrase; the switch turns itself off the moment the rerun starts. Every run, refusal and switch change is recorded and audited.",
        "A run that failed or was cancelled can be resumed with the phrase “resume <slug>”. Nothing is ever removed: permissions are only added, existing secrets and certificates are kept, and role changes are shown before they are made.",
      ],
    },
  ],
};

const CONSENT_HELP: PageHelp = {
  title: "Admin consent",
  purpose: "The permissions this product asks for, and which of them are actually in force.",
  sections: [
    {
      title: "Which rows are listed",
      body: [
        "Only what the modules switched on need — a module held off because the tenant has no " +
          "licence for it is left out, one whose licence nobody has read yet is kept — plus " +
          "the core capabilities every scan uses. A Manual role (Exchange RBAC, the Purview " +
          "role group, the sidecar certificate) is an instruction for a person: no run ever " +
          "marks it done.",
      ],
    },
    {
      title: "The trap this screen exists for",
      body: [
        "The consent link grants exactly the permissions configured on the application " +
          "registration — it carries no permission list of its own. A permission missing " +
          "from the registration is missing from Microsoft's prompt too, consent succeeds " +
          "anyway, and nobody sees an error until a check reports “not collected” weeks " +
          "later.",
        "So the order is: add the permission to the registration first, then follow the " +
          "link. The state beside each permission is what the probe MEASURED, so the list " +
          "and the tenant cannot drift apart unnoticed.",
      ],
    },
    {
      title: "The states",
      terms: [
        {
          term: "Granted",
          meaning: "The last probe reached every capability behind this permission.",
        },
        {
          term: "Missing",
          meaning:
            "The probe found it absent. The detail beside it says whether granting the " +
            "permission is the fix; Tenant init & updates adds it to the app registration.",
        },
        {
          term: "Not measured",
          meaning: "The credential has never been verified, so nothing has been measured.",
          caution: "It is not “missing”. Run Verify under Entra application.",
        },
      ],
    },
  ],
};

const CAPABILITIES_HELP: PageHelp = {
  title: "Capabilities",
  purpose: "What the credential can actually see, probed rather than assumed.",
  sections: [
    {
      title: "How a capability is decided",
      body: [
        "Three layers, in order: the token's roles claim, the directory's own record of " +
          "what was granted, and one cheap live call. The live call wins — a permission that " +
          "is granted and refused by the service is not a capability.",
      ],
    },
    {
      title: "The reasons",
      terms: [
        {
          term: "insufficient_permission",
          meaning: "The grant is genuinely absent. Adding it is the fix.",
        },
        {
          term: "license_missing",
          meaning: "The tenant does not own the product.",
          caution:
            "Granting the permission changes nothing here and only widens the registration. " +
            "It is the most common wasted step in onboarding.",
        },
        {
          term: "connector_auth_failed",
          meaning:
            "The service refused the token for its own reasons — an Exchange or Purview role " +
            "the Entra consent does not convey.",
        },
        {
          term: "Unknown",
          meaning: "The probe could not run at all.",
          caution:
            "Recorded as unknown, never as granted. An unreachable probe is not evidence " +
            "either way.",
        },
      ],
    },
    {
      title: "What to do",
      body: [
        "Read the reason before granting anything. Only insufficient_permission is fixed by " +
          "a consent; the others point at a licence or at a role group in another portal, and " +
          "docs/entra-app-registration.md walks through both.",
      ],
    },
  ],
};

/** A block's sections, each title prefixed with the block's name. */
function inBlock(block: string, help: PageHelp): HelpSection[] {
  return help.sections.map((section) => ({ ...section, title: `${block}: ${section.title}` }));
}

export const PAGE_HELP: Record<string, PageHelp> = {
  tenant_connection: {
    title: "Tenant connection",
    purpose:
      "How this console reads the tenant: the credential, how it was set up, what it is allowed to read, and what it can actually see.",
    sections: [
      {
        title: "The four blocks",
        body: [
          "Entra application holds the credential and Verify. Tenant init & updates sets the tenant up with a Global Administrator's sign-in, and later adds what a newly switched-on module needs. Permissions & admin consent lists every permission and role the modules switched on need, with the state the last probe measured. Capabilities shows the probe itself and the checks each gap costs.",
          "Each block starts closed and says in one line what is inside it. The line is read, never assumed: a status that could not be read says “Status unavailable”, and a permission the probe could not measure is counted as unknown — never as granted, never as missing.",
        ],
      },
      ...inBlock("Entra application", CREDENTIALS_HELP),
      ...inBlock("Tenant init", TENANT_INIT_HELP),
      {
        title: "Tenant init: adding permissions later",
        body: [
          "Switching a module on, or a licence turning up for one, can mean permissions or roles the scanner app does not hold yet. The module's card then says how many are still to grant, with a Grant now link to this block; nothing opens by itself.",
          "Apply missing permissions adds exactly what is missing to the app registration the completed run recorded — after a Global Administrator's MFA sign-in and a confirmation listing every addition. It never mints a secret, never rotates the credential and never removes anything, so it needs no Dangerous switch and no typed phrase. Manual items (Exchange RBAC, the Purview role group, the sidecar certificate) stay manual instructions.",
          "If the setup app was deleted after the first run, the block says so and points at the setup-app repair; a sign-in needs a setup app.",
        ],
      },
      ...inBlock("Permissions & admin consent", CONSENT_HELP),
      ...inBlock("Capabilities", CAPABILITIES_HELP),
    ],
  },
  overview: {
    title: "Overview",
    purpose: "How this tenant is doing right now, and which way it is moving.",
    sections: [
      {
        title: "What you are looking at",
        body: [
          "The result of one scan run — by default the last one that FINISHED. While a scan " +
            "is running these figures are from the previous one and the page says so, because " +
            "a console that went to zero mid-scan would read as “nothing is wrong”.",
          COVERAGE_RULE,
        ],
      },
      {
        title: "The numbers",
        terms: [
          {
            term: "The four buckets",
            meaning:
              "The overview groups the scores by criticality into four buckets: Critical & " +
              "high (the sp-ms365 rule set's critical and high checks), Medium & low (its " +
              "medium, low and informational checks), AI best practice (this product's own " +
              "AI framework, when its module is on) and Microsoft Secure Score (Microsoft's " +
              "own number, an independent yardstick shown as a cross-check). " +
              TWO_SCORES +
              " The rule set's percentages are weighted by severity; every AI best practice " +
              "control weighs 1; Microsoft Secure Score is earned over maximum points, which is " +
              "already a score over everything, so pass vs executed does not apply to it.",
            caution:
              "A bucket is a heading, never a number: every score carries its own coverage over " +
              "its own checks, and scores are never blended or averaged. There is no overall " +
              "score. The two rule-set buckets never share a check. Each bucket is also the " +
              "first four tabs of Posture, in this order. The Microsoft baseline is not a " +
              "bucket; it has its own tab under Posture, after the buckets.",
          },
          {
            term: "Switched off",
            meaning:
              "Microsoft Secure Score, AI best practice and the Microsoft baseline each have a " +
              "switch. One that is switched off shows exactly “Switched off” and where to " +
              "switch it on — no score, no “not collected”, no “not measured” and no hatch. " +
              "Switched off is the operator's choice; not collected is a check that could not " +
              "run; not scored is a framework that is switched on but produced no score in " +
              "this run. Secure Score switched off is not read at all.",
            caution:
              "When the setting itself cannot be read the card says so — the switch state is " +
              "unknown, which is never shown as off.",
          },
          {
            term: "CIS references",
            meaning:
              "The former Foundations benchmark (CIS references) was folded into the rule " +
              "set. There is no CIS score any more; where a check was mapped to a CIS " +
              "control, its detail shows a muted “Reference: CIS …” line.",
          },
          {
            term: "Coverage",
            meaning:
              "How much of what COULD have been measured actually was. Checks that could not " +
              "run are in the denominator and not the numerator, which is what drags it down.",
          },
          {
            term: "Microsoft Secure Score",
            meaning:
              "Microsoft's own score, shown beside comparable organisations. The comparison " +
              "axis is PERCENT — your 60 % against their 46 % — not points.",
          },
          {
            term: "How things are moving",
            meaning:
              "Each headline number against the same number a day, a week, a month and a " +
              "year ago.",
            caution:
              "A period with no retained scan behind it says “no scan”, never “no change”. A " +
              "delta of zero where nothing was there to compare against would read as " +
              "stability. Retention keeps one run per week, month and year, so the year " +
              "comparison can resolve — against the nearest retained run, whose true age is " +
              "shown.",
          },
        ],
      },
      {
        title: "What to do",
        body: [
          "Start with the failing checks of the highest severity: Posture → Findings, sorted " +
            "worst first. Then look at coverage — a low coverage usually means a missing " +
            "permission, and Administration → Tenant connection → Capabilities says which.",
        ],
      },
    ],
  },

  posture_baseline: {
    title: "Posture — Microsoft baseline security mode",
    purpose:
      "How much of Microsoft's published minimum security baseline this tenant is actually " +
      "running.",
    sections: [
      {
        title: "What it measures, and what it does not",
        body: [
          "Baseline security mode is a set of switches in the Microsoft 365 admin centre, " +
            "under Settings \u2192 Org settings \u2192 Security & privacy. Microsoft describes " +
            "them as its minimum security benchmark recommendations, and an administrator turns " +
            "them on one at a time across Microsoft 365 apps, SharePoint and OneDrive, Teams, " +
            "Exchange Online and Microsoft Entra.",
          "This console never reads those switches. There is no documented API that reports " +
            "the Baseline security mode toggles, so every control here is judged on the " +
            "underlying tenant configuration a toggle produces \u2014 the Conditional Access " +
            "policy, the authorization policy, the Exchange organisation setting, the " +
            "SharePoint tenant setting. The question this lane answers is \u201chow much of " +
            "Microsoft's minimum baseline is this tenant actually running\u201d, not " +
            "\u201cwhich switches are flipped\u201d.",
          "The difference is not academic in either direction. A tenant that already had the " +
            "underlying configuration in place satisfies a control without ever opening that " +
            "page, and this lane says so. A tenant that flipped a switch whose effect has not " +
            "reached the configuration this console can read does not get credit for the " +
            "switch.",
        ],
      },
      {
        title: "Why the coverage is low, and why that is the measurement",
        body: [
          COVERAGE_RULE,
          "It bites hardest here. Microsoft publishes twenty-one settings and many of them " +
            "have no application-readable state at all. Nine are delivered by the Cloud Policy " +
            "service for Microsoft 365, whose only documented surfaces are two admin centres " +
            "and a manual CSV export; three more live solely on SharePoint PowerShell " +
            "parameters this console holds no connector for. Those controls are still counted.",
          "The two Teams Rooms controls are the exception that shows what the bar is. Both are " +
            "Conditional Access policies over the room resource accounts, and Microsoft leaves " +
            "the group that holds those accounts to each tenant to name, so matching a policy " +
            "by its name would have been a guess. They are measured now because the resource " +
            "accounts are identified from their Teams Rooms licences and each account's own " +
            "group membership is read, which resolves a policy's scope instead of inferring it.",
          "So a full score on this card can sit over a handful of measured controls. Read the " +
            "coverage first: it is the share of Microsoft's own control set that produced a " +
            "verdict, and until it rises the score is a statement about a small part of the " +
            "baseline rather than about the baseline.",
        ],
        terms: [
          {
            term: "Not collected",
            meaning:
              "The control's state has no readable source, or sp-ms365 has not written the " +
              "rule yet. It carries the reason, stays in the coverage denominator and drags " +
              "coverage down.",
            caution:
              "It is never a pass, never a failure and never a zero. Dropping these controls " +
              "instead would leave a coverage figure that flattered the measurement behind it.",
          },
          {
            term: "Declared",
            meaning:
              "The count of controls with no check behind them at all. It is the size of the " +
              "gap between what Microsoft publishes and what this console can see.",
          },
          {
            term: "Retired by Microsoft",
            meaning:
              "Microsoft has withdrawn the capability the control guards against, so no tenant " +
              "can still have it turned on. The control is not applicable rather than " +
              "unmeasured, and it leaves the coverage count instead of holding it down.",
            caution:
              "This is the one status that takes a control out of the denominator, so it is " +
              "only used where Microsoft states the capability is gone. The statement is quoted " +
              "in the control's reason, so the claim can be checked rather than trusted.",
          },
          {
            term: "Version",
            meaning:
              "The date Microsoft's settings page was read. Microsoft gives the page no " +
              "version number of its own, so the date is the version \u2014 a control " +
              "reference means \u201cthe setting of that name on that page on that date\u201d.",
          },
        ],
      },
      {
        title: "How it relates to the other scores",
        body: [
          "This is the Microsoft baseline score. It is shown here and in the report's baseline " +
            "section, not on the Overview, and is never blended with the sp-ms365 rule set's " +
            "buckets or with Microsoft Secure Score. They are measurements over different " +
            "control sets, and one number over all of them would measure none of them.",
          "It also carries no level split: Baseline security mode publishes none, and " +
            "inventing one would put an sp-ms365 opinion under Microsoft's name.",
          "The framework ships switched off, and then its card says exactly “Switched off” " +
            "and nothing else. An administrator turns it on under Settings \u2192 Scoring, " +
            "because a card that reads full marks at low coverage is worth showing to somebody " +
            "who asked for it and misleading to somebody who did not.",
        ],
      },
    ],
  },

  posture_ai_bp: {
    title: "Posture — AI best practice",
    purpose:
      "Where this tenant and its Azure subscriptions stand against this product's AI best " +
      "practice framework, built from Microsoft's published recommendations — and how much " +
      "of it any API can show.",
    sections: [
      {
        title: "Whose framework this is",
        body: [
          "AI best practice — Microsoft 365 & Azure is this product's own framework. Each of " +
            "its 148 controls is a configuration Microsoft recommends on learn.microsoft.com, and " +
            "each row links to the Learn pages it was built from. It is not a Microsoft " +
            "framework, and Microsoft does not publish or endorse this list.",
          "The date beside the score is the day those pages were read. Microsoft gives them no " +
            "version number of their own, so the date is the version.",
        ],
      },
      {
        title: "What it measures, and what it cannot",
        body: [
          "The controls are grouped into seven areas: Copilot data protection, oversharing, " +
            "agents and identity, Copilot Studio / Power Platform, Azure AI Foundry / Azure " +
            "OpenAI, Azure AI Search, and monitoring and threat protection. An area is a " +
            "grouping, not a level: there is no score per area. Controls are in numeric order " +
            "inside each area.",
          "This console reads what the APIs expose: Purview, SharePoint and Exchange settings; " +
            "agent identities and their permissions in Entra; Copilot agents; Azure AI resources " +
            "through Azure Resource Manager with the Reader role; Foundry projects through their " +
            "data plane with a custom read-only role; Copilot Studio agents through the Power " +
            "Platform API and each environment's Dataverse; and Defender configuration. Each of " +
            "those is a separate grant, and one that is missing leaves its checks not collected.",
          "The framework scores configuration only. Organisational practices — reviews, " +
            "deployment order, incident plans — are not controls, and nothing here asks for " +
            "a manual attestation.",
        ],
      },
      {
        title: "Reading a control",
        terms: [
          {
            term: "Fail",
            meaning:
              "A check behind the control measured a failure. The count of failing controls is " +
              "the headline.",
          },
          {
            term: "Severity",
            meaning:
              "Critical, high, medium or low — this product's judgement of the control, " +
              "shown on every row.",
            caution: "Severity orders the list. It is not a weight: every control counts once.",
          },
          {
            term: "Readable part passes",
            meaning:
              "Every check that can read part of the control passes, but the control is broader " +
              "than any setting an API exposes. The check can sink the control and never " +
              "satisfy it, so the control is not collected.",
            caution: "It is not a pass. Counting it as one would score the readable minority.",
          },
          {
            term: "No readable source / Check pending",
            meaning:
              "Declared: no documented app-only API exposes what the recommendation asks about, " +
              "or the check is not written yet. The reason is quoted on the row.",
          },
          {
            term: "Collection gap",
            meaning:
              "A check behind the control could not run — a permission, a licence, a preview " +
              "switch, or an Azure subscription without Reader. This is the one an operator " +
              "can usually close.",
          },
          NOT_COLLECTED,
          NOT_APPLICABLE,
        ],
      },
      {
        title: "Why the coverage is low, and why that is the measurement",
        body: [
          COVERAGE_RULE,
          "Many mapped controls are partial, and many recommendations have no readable source " +
            "yet. Those controls count against pass vs all, which is why that headline is low " +
            "while pass vs executed — over the controls that could be read — can be high.",
          "Every control stays in the denominator, including the ones nothing can read. Dropping " +
            "them would leave a coverage figure that flattered the measurement behind it.",
        ],
      },
      {
        title: "The Azure scope",
        body: [
          "Azure Resource Manager lists only the subscriptions this application holds a role on. " +
            "Without a statement of which subscriptions should be visible, one nobody granted " +
            "Reader on would be silently absent. Settings → Modules holds that list " +
            "(azure_subscription_ids) under the AI best practice module.",
          "With the list set, a listed subscription that is not visible makes every Azure-backed " +
            "check not collected — never a pass over the rest. With it empty, checks are " +
            "measured over what the credential can see, and the scope line says so. Where the " +
            "visible subscriptions were not measured at all, the scope line says that instead of " +
            "showing an empty list.",
          "Reader is an Azure role assignment, not an application permission: the consent link " +
            "cannot grant it. The admin consent page shows the instruction and what the last " +
            "probe measured for each Azure capability.",
        ],
      },
      {
        title: "How it relates to the other scores",
        body: [
          "The AI best practice score is its own. It is never blended with the sp-ms365 rule " +
            "set (Critical & high or Medium & low), Microsoft Secure Score or the Microsoft " +
            "baseline, and the " +
            "module's own checks are never part of the rule set's scores, with the module on " +
            "or off. Rule-set checks the framework maps to keep counting in the rule set's " +
            "scores as before.",
          "The framework belongs to the AI best practice module, which ships switched off. Off, " +
            "there is no AI best practice report section or CSV, and its Posture tab, the " +
            "overview card, the printed report and the exports say exactly “Switched off” with " +
            "where to switch it on (Settings → Modules) — never a score or a gap. A run scanned while the module " +
            "was off has no assessment, and this page then says so instead of showing a page of " +
            "empty controls.",
        ],
      },
    ],
  },

  posture: {
    title: "Posture — buckets and findings",
    purpose: "Every control this deployment evaluated, and what each one found.",
    sections: [
      {
        title: "The tabs",
        body: [
          "The first four tabs are the overview's four buckets, in the same order: Critical & " +
            "high, Medium & low, AI best practice and Microsoft Secure Score. Critical & high " +
            "and Medium & low each show the bucket's two scores — pass vs all applicable checks " +
            "and, beneath it, pass vs executed — with its coverage, the same numbers " +
            "as the overview — and below it the bucket's findings. Their chips and their list " +
            "count exactly the checks that score counts: the sp-ms365 rule set's checks of " +
            "that severity (informational is in Medium & low), without the manual " +
            "attestations and without the AI best practice module's own checks.",
          "Set apart after them: All findings (every check of the run), Microsoft baseline (a " +
            "framework, not a bucket), Manual attestations and Exceptions.",
          "A switched-off framework's tab — AI best practice, Microsoft Secure Score or the " +
            "Microsoft baseline — shows exactly “Switched off” and where to switch it on, and " +
            "nothing else.",
        ],
      },
      {
        title: "The filters",
        terms: [
          {
            term: "State",
            meaning: "Restricts to one result state. The states are described below.",
          },
          {
            term: "Severity",
            meaning: "Critical, high, medium or low, as the control catalogue defines it.",
          },
          {
            term: "Framework",
            meaning:
              "Restricts to checks mapped to a framework — Microsoft's baseline security " +
              "mode, for instance. CIS is not a framework here any more: its control numbers " +
              "are references on a check, shown in its detail.",
            caution:
              "A framework score is computed over ITS controls, not over all checks, so it " +
              "is not comparable with the Critical & high or Medium & low bucket scores.",
          },
          {
            term: "Reason",
            meaning:
              "Restricts to one reason code — the query behind “show me everything I could " +
              "not measure because of a missing permission”.",
          },
          {
            term: "Search",
            meaning:
              "Matches the check id and the reason detail. It deliberately does not search " +
              "titles: those are resolved at render time and are not in the database.",
          },
        ],
      },
      {
        title: "The states",
        terms: [
          { term: "Pass", meaning: "The control is in place, measured on this run." },
          {
            term: "Fail",
            meaning:
              "The control is not in place. The affected count says how many objects are " +
              "involved.",
          },
          NOT_COLLECTED,
          NOT_APPLICABLE,
          ACCEPTED,
          {
            term: "Awaiting attestation",
            meaning:
              "A control nothing can answer automatically. Somebody has to confirm it and " +
              "say how they know.",
          },
          {
            term: "Attested & documented",
            meaning:
              "Somebody has confirmed it, with a note and their name. It counts in the " +
              "manual lane, which is scored separately from the measured one.",
            caution:
              "Self-attestation is never mixed into the measured score. An answer on " +
              "somebody's word is not the same evidence as a reading from an API.",
          },
        ],
      },
      {
        title: "What to do",
        body: [
          "Open a finding to see the evidence: the criterion it applied, the objects it " +
            "found wanting, and the limitation of what it examined. The remediation names the " +
            "exact screen and setting.",
          "Where a finding is real and will not be fixed, accept it with an owner, a reason " +
            "and an expiry — that is a decision on the record, not a way to hide it. Where it " +
            "could not run, the reason says whether a permission, a licence or a module switch " +
            "is the cause.",
        ],
      },
    ],
  },

  inventory: {
    title: "Inventory",
    purpose: "What is actually in this tenant, as the last scan found it.",
    sections: [
      {
        title: "What you are looking at",
        body: [
          "One row per object, grouped by kind. Every value shown was read from Microsoft's " +
            "API; nothing here is derived or estimated.",
          "Deleted objects are KEPT with a deletion date rather than removed. A deletion is " +
            "signal — an account that disappeared last week is often the most interesting row " +
            "on the page.",
        ],
      },
      {
        title: "Reading a value",
        terms: [
          {
            term: "A blank or “not collected”",
            meaning:
              "The property was not read. That can be a permission, an API version, or a " +
              "property Microsoft excludes from change tracking.",
            caution: "It is never rendered as 0 or as “false”. Absent is not a value.",
          },
          {
            term: "An absent property on a row that WAS read",
            meaning:
              "Microsoft omits null-valued properties rather than sending an explicit null. " +
              "On a row that arrived, an absent field is therefore a measurement — “never " +
              "synced”, not “not collected”.",
          },
          {
            term: "State summary",
            meaning:
              "Stale devices, stale users, directory-synced accounts and their source domains. " +
              "Every facet is an exhaustive partition with an explicit unknown bucket.",
            caution:
              "The unknown bucket is the population whose value could not be established. It " +
              "is never folded into one of the real answers.",
          },
        ],
      },
      {
        title: "Groups that belong to a module",
        body: [
          "Some card groups exist only while their module is in effect. A group whose module " +
            "is off is removed from this page rather than shown as empty cards: nothing was " +
            "collected because nobody asked for it, which is a configuration and not a " +
            "coverage gap.",
        ],
        terms: [
          {
            term: "SharePoint & OneDrive",
            meaning:
              "The tenant-wide SharePoint and OneDrive settings — one singleton row — and the " +
              "site collections, read through Microsoft Graph. The settings card was under " +
              "Policy before the module existed; it is the same object, moved.",
            caution:
              "The site list is a full read on every scan, not a change stream. A site that " +
              "disappeared is kept with a deletion date, like every other kind here.",
          },
          {
            term: "Microsoft Teams",
            meaning:
              "The teams, with their guest and member settings, and every channel of each " +
              "team, read through Microsoft Graph. The module ships OFF: it needs four " +
              "application permissions nothing else here uses.",
            caution:
              "The tenant-level Teams policies — federation, meetings, messaging — have no " +
              "application-permission Graph route and are NOT here. Their checks sit in the " +
              "manual attestation lane.",
          },
        ],
      },
    ],
  },

  changes: {
    title: "Changes",
    purpose: "What moved between two scan runs.",
    sections: [
      {
        title: "How a change was learned",
        terms: [
          {
            term: "delta",
            meaning:
              "Microsoft told us directly, through a change-tracking stream. The most reliable " +
              "kind of evidence here.",
          },
          {
            term: "snapshot_diff",
            meaning:
              "We compared two scans and the value differed. It says the value changed between " +
              "those two runs, not when.",
            caution:
              "Not every property can ride a change stream — sign-in activity and mailbox " +
              "settings among them — so their changes can only ever be a snapshot difference.",
          },
          {
            term: "Thinned runs",
            meaning:
              "When retention deletes an old run, its change events are moved to the nearest " +
              "surviving run at or after it. The events keep their own time and source; only " +
              "the run they are filed under changes.",
            caution:
              "A surviving run's count therefore covers the whole thinned interval, and a " +
              "comparison between two survivors stays complete. Comparing against a run id " +
              "that has been thinned answers “not found”.",
          },
        ],
      },
      {
        title: "What to do",
        body: [
          "Read it after an incident, and before an audit. A privileged role appearing, a " +
            "policy being disabled, or a forwarding rule created are the rows worth an alert.",
        ],
      },
    ],
  },

  scans: {
    title: "Scans",
    purpose: "Every collection run, what it managed to read, and what it could not.",
    sections: [
      {
        title: "Run status",
        terms: [
          { term: "completed", meaning: "Every planned collector ran." },
          {
            term: "partial",
            meaning:
              "Some collectors ran and some did not. The results it produced are real; the " +
              "gaps are named.",
          },
          {
            term: "failed",
            meaning:
              "The run stopped. What it collected before stopping is still stored and still " +
              "dated.",
          },
          {
            term: "Progress",
            meaning:
              "Collectors done out of the number PLANNED. A collector the capability probe " +
              "gated out was never planned work and is not counted in either.",
          },
          {
            term: "Gaps in the list",
            meaning:
              "Runs older than the keep-all window are thinned to one per week, then one per " +
              "month, then one per year (Administration → Retention). A missing run between " +
              "two old ones was deleted by that cleanup, not lost.",
            caution:
              "A surviving run inherits the change events of the runs thinned before it, so " +
              "its change count covers the whole interval. A link to a thinned run answers " +
              "“not found”; the History page lists what is still stored.",
          },
        ],
      },
      {
        title: "What to do",
        body: [
          "A run that is repeatedly partial points at a permission or a connector rather than " +
            "at the tenant. Administration → Tenant connection → Capabilities says which capability failed and " +
            "carries the service's own error text.",
        ],
      },
    ],
  },

  history: {
    title: "History",
    purpose: "How score, coverage and tenant size moved across the retained scan runs.",
    sections: [
      {
        title: "What you are looking at",
        body: [
          "One point per scan run that retention still holds: every run for the last few " +
            "days, then one per week, one per month and one per year, plus the runs the " +
            "cleanup never deletes. The buckets are drawn: Critical & high and Medium & low " +
            "(the sp-ms365 rule set split by severity, informational included in Medium & " +
            "low) and, " +
            "when its module scored the run, AI best practice — each with its coverage and " +
            "as the Overview computes it, severity weights applied and manual attestations " +
            "excluded, so the lines and the cards agree for the same run. Both rule-set " +
            "buckets are computed from the stored results, so their lines reach back over " +
            "every retained run.",
          "Each line is the bucket's pass vs all applicable checks (D32), the headline the " +
            "Overview shows; the table gives the same number per run. Pass vs executed is on " +
            "the Overview and in the report beside it.",
          "Older runs scored the Foundations benchmark (CIS references) as well. That " +
            "framework was folded into the rule set, so those points are no longer drawn; " +
            "the stored data is untouched.",
          COVERAGE_RULE,
          "Accepted risks are applied as they stand now, to every point. A finding accepted " +
            "last month reads as accepted on last year's run too; the stored results are " +
            "not rewritten, only scored against today's exceptions.",
        ],
      },
      {
        title: "Reading the chart",
        terms: [
          {
            term: "A gap in the line",
            meaning:
              "A run that produced no score — it failed, was cancelled, or measured nothing. " +
              "It is still a point, marked on the axis and named under the chart.",
            caution:
              "It is not a drop to zero. A line that fell to 0 % on a failed run would claim " +
              "the tenant failed everything on a day nothing was measured.",
          },
          {
            term: "Coverage",
            meaning:
              "Drawn with every score, on the right axis. A score at 40 % coverage and the " +
              "same score at 98 % are different claims; the line alone would hide which.",
          },
          {
            term: "Checks by state",
            meaning:
              "How many checks passed, failed, were accepted or could not run on each run. " +
              "The not-collected bar is hatched because it is not a measured category.",
            caution:
              "A state a run did not report is absent, not 0.",
          },
          {
            term: "Tenant object counts",
            meaning:
              "Users, groups and devices as counted by the run. An incremental pass counts " +
              "nothing and leaves a gap; only a full collection carries a count.",
          },
          {
            term: "Changes",
            meaning:
              "Change events filed under the run, including those moved to it from runs the " +
              "cleanup deleted before it — so a survivor's count covers the whole interval.",
          },
        ],
      },
      {
        title: "What to do",
        body: [
          "A falling score with steady coverage is the tenant getting worse; a falling score " +
            "with falling coverage is usually a permission or connector that stopped working. " +
            "Check the Scans page for the run and Administration → Tenant connection → Capabilities for the cause.",
          "Retention is set under Administration → Retention, where the cleanup can also be " +
            "previewed and run.",
        ],
      },
    ],
  },

  defender: {
    title: "Defender",
    purpose: "The posture of the five Defender products: their findings, the remediation for each, and which ones this tenant holds.",
    sections: [
      {
        title: "Why five separate rows",
        body: [
          "Defender is one name for five products a tenant buys independently. Each is " +
            "licensed separately, so each is a module of its own — and every one is listed, " +
            "including the ones with no licence. Leaving those out would turn “this tenant " +
            "does not have it” into silence, and silence reads as “nothing wrong”.",
        ],
      },
      {
        title: "The states",
        terms: [
          {
            term: "In effect",
            meaning: "Switched on and licensed. Its checks run and its findings appear.",
          },
          {
            term: "Switched off",
            meaning:
              "An operator turned it off. Everything it previously collected is kept — turning " +
              "a module off is not a decision to destroy its evidence.",
          },
          {
            term: "No licence",
            meaning:
              "The tenant's subscriptions carry no service plan for this product. The module " +
              "is held off automatically.",
            caution:
              "Licences are read from the service PLANS inside each subscription, so one " +
              "arriving through a bundle such as Microsoft 365 E5 counts like a standalone " +
              "one. Several plans look like a product and are not — Cloud Discovery is not " +
              "Defender for Cloud Apps, and Defender for Business is neither Endpoint tier.",
          },
          {
            term: "Not measured",
            meaning:
              "No subscription has been collected yet, so the licence is an open question.",
            caution:
              "This never disables a module. Nobody looked is not the same as having none.",
          },
        ],
      },
      {
        title: "The findings",
        body: [
          "A product in effect lists every check the last run evaluated for it, worst first: " +
            "failures, then checks that could not run, then accepted risk. Passing checks are " +
            "folded under a count so the rows to act on come first. Each failing row carries " +
            "the remediation, and its title opens the full finding with evidence.",
        ],
        terms: [
          {
            term: "N affected",
            meaning: "How many objects the check found in the wrong state.",
            caution: "Shown only when the collector counted. A missing count is absent, never 0.",
          },
          {
            term: "Not collected",
            meaning:
              "The check could not run and says why — usually a permission. It is excluded " +
              "from the counts of failures and passes.",
          },
        ],
      },
      {
        title: "What to do",
        body: [
          "Fix the failures product by product using the remediation on each row. Switches " +
            "and licences are on Administration → Modules; a module that is switched on but " +
            "held off by the licence says so there. The printable Defender report is the same " +
            "view with every finding, for the run shown.",
        ],
      },
    ],
  },

  reports: {
    title: "Reports",
    purpose: "The findings as a file, and the print-ready documents.",
    sections: [
      {
        title: "What each export is",
        terms: [
          {
            term: "Findings CSV",
            meaning:
              "One row per evaluated check: its state, the reason code, the affected count " +
              "where one was established, and the endpoint the evidence came from. The filter " +
              "narrows it; it never pages it — the file is the whole filtered set.",
          },
          {
            term: "Posture report",
            meaning:
              "The print-ready document, with an appendix of everything that could not be " +
              "measured and why.",
            caution:
              "That appendix is what makes the score readable, so it is always included and " +
              "there is no option to remove it.",
          },
          {
            term: "Defender report",
            meaning:
              "The five Defender products separately, because they are licensed separately. " +
              "It appears only where a Defender product is in effect.",
          },
        ],
      },
    ],
  },

  modules: {
    title: "Modules",
    purpose: "Which parts of this console this deployment has.",
    sections: [
      {
        title: "What a module switch does",
        body: [
          "It decides whether a whole part of the console EXISTS: its pages, its navigation, " +
            "its API and the work behind it. A disabled module's pages are removed rather than " +
            "greyed out — a greyed entry advertises something this deployment has decided it " +
            "does not have.",
          "Nothing already recorded is deleted. Switch a module back on and its history is " +
            "there.",
        ],
      },
      {
        title: "Enabled, configured, unavailable",
        terms: [
          {
            term: "Enabled",
            meaning: "In effect: switched on AND licensed where a licence is needed.",
          },
          {
            term: "Unavailable",
            meaning:
              "Switched on, but this tenant holds no licence for it. The switch is not broken; " +
              "the licence is what is holding it.",
          },
          {
            term: "Not measured",
            meaning:
              "The licence could not be established because no subscription has been collected.",
            caution: "This never disables a module.",
          },
        ],
      },
      {
        title: "Modules with no page of their own",
        body: [
          "Not every module adds a menu entry. Two of them add an inventory card group and a " +
            "set of checks, and are otherwise invisible; what their switch removes is that " +
            "group and those checks.",
        ],
        terms: [
          {
            term: "SharePoint & OneDrive",
            meaning:
              "Ships ON, unlike the other licensed modules: its tenant-settings collector and " +
              "its checks were part of the product before the module existed, and a default " +
              "of off would have silently dropped them on upgrade. Switched off, the group " +
              "leaves the Inventory page and its checks report as not collected.",
            caution:
              "Not collected is not a pass. Turning the module off lowers coverage; it does " +
              "not raise the score.",
          },
          {
            term: "Microsoft Teams",
            meaning:
              "Ships OFF. It reads the teams and their channels through Microsoft Graph and " +
              "evaluates the Teams checks Graph can answer. The tenant-level Teams policies " +
              "have no application-permission route and stay in the manual lane whether the " +
              "module is on or off.",
          },
        ],
      },
    ],
  },

  emergency_access: {
    title: "Emergency access",
    purpose: "The accounts that must survive a tenant lockout, declared rather than guessed.",
    sections: [
      {
        title: "Why declare them",
        body: [
          "Until an account is declared here, this console INFERS which are emergency access: a " +
            "user excluded from an enabled Conditional Access policy, because that exclusion is " +
            "what makes an account break-glass.",
          "The inference has two blind spots pointing in opposite directions. A service account " +
            "excluded for some unrelated reason is counted as an emergency account, which puts a " +
            "false name on an incident report. And an emergency account excluded from NO policy " +
            "is invisible to it — which is the worse of the two, because that account is itself " +
            "the finding: it will be locked out by the very policy it exists to survive, and " +
            "nobody discovers that until the day it is needed.",
        ],
      },
      {
        title: "What is stored, and how",
        terms: [
          {
            term: "User principal name",
            meaning: "Encrypted at rest, with the same cipher as the tenant credentials.",
            caution:
              "The list of a tenant's emergency accounts is the shortest list of names worth " +
              "attacking. It is sealed from a stolen backup, not from you.",
          },
          {
            term: "Comment",
            meaning:
              "Why the account exists and where its credential is kept. Encrypted, and returned " +
              "by this screen only.",
            caution:
              "It never appears in a finding, a report or a CSV. A finding names the account; the " +
              "safe holding its key is not part of a security report.",
          },
          {
            term: "Withdrawing an entry",
            meaning:
              "Marks it withdrawn and keeps the row. That an account WAS emergency access and no " +
              "longer is, is exactly what an auditor asks about.",
          },
        ],
      },
      {
        title: "What it enables",
        body: [
          "Declaring an account turns on ms365.entra.break_glass_declared_excluded — the check " +
            "that cannot be made by inference. It verifies that every declared account is excluded " +
            "from at least one enabled Conditional Access policy.",
          "The user principal name cannot be edited afterwards. Changing which account an entry " +
            "names, while keeping its id and history, would rewrite what an earlier scan reported " +
            "— withdraw it and declare the new one, so both stay on the record.",
        ],
      },
    ],
  },

  settings: {
    title: "Settings",
    purpose: "The values this deployment reports against.",
    sections: [
      {
        title: "Before you change one",
        body: [
          "Every setting here changes what this product REPORTS, not what the tenant is. " +
            "Lowering a threshold does not improve a tenant; it changes the line a finding is " +
            "measured against, and every scan after it is judged by the new line.",
          "A change is recorded in the audit log with who made it and when.",
        ],
      },
      {
        title: "The ones most often misjudged",
        terms: [
          {
            term: "enabled_frameworks",
            meaning:
              "The frameworks that are still a choice, shown as two switches: " +
              "“Microsoft baseline” (ms_baseline) and “Microsoft Secure Score” " +
              "(secure_score). An empty list is valid. The sp-ms365 rule set is always " +
              "on, so own is not a value here any more, and cis_m365 was removed when the " +
              "Foundations benchmark was folded into the rule set.",
            caution:
              "A stored own or cis_m365 was stripped by migration 000116; the server now " +
              "refuses either with a message that says why.",
          },
          {
            term: "retention_keep_days",
            meaning:
              "Every finished run inside the last N days is kept, whatever its status. " +
              "Seven by default. This is the finest tier and must be at least 1.",
            caution:
              "Below fourteen days, keep retention_keep_weeks at 1 or more, or the week-ago " +
              "KPI comparison can have no run to land on.",
          },
          {
            term: "retention_keep_weeks / _months / _years",
            meaning:
              "Beyond the keep-all window, one run per ISO week, one per calendar month and " +
              "one per calendar year for that many periods (UTC buckets). Four weeks, twelve " +
              "months and ten years by default; 0 disables a tier.",
            caution:
              "A finer tier wins: a period already represented by a kept run picks nothing " +
              "extra. Within a bucket the representative is the newest completed run, then " +
              "partial, then failed, then cancelled — so a period's witness is a run that " +
              "measured something whenever one exists.",
          },
          {
            term: "retention_cleanup_cron",
            meaning:
              "When the worker runs the cleanup, as a cron expression in the container's " +
              "local time. Daily at 03:00 by default. Evidence older than " +
              "retention_evidence_days is blanked in the same pass.",
            caution:
              "The cleanup can also be run from the card at the top of this page. Both do " +
              "exactly what the preview lists, and both are recorded in the audit log.",
          },
          {
            term: "What is never deleted",
            meaning:
              "A run still being written, the newest finished run whatever its age, and any " +
              "run an inventory object was first or last seen in. The preview counts these " +
              "separately so nobody can claim “one per month” where the truth is more.",
            caution:
              "A deleted run's change events are moved to the next surviving run, not lost. " +
              "Its check results and metrics are per-run measurements and go with it.",
          },
          {
            term: "preview_apis_enabled",
            meaning:
              "Whether beta and preview Microsoft surfaces are contacted at all. Off by " +
              "default.",
            caution:
              "A collector behind it reports itself skipped with a reason rather than leaving " +
              "the data quietly empty.",
          },
          {
            term: "stale_object_days",
            meaning: "The age past which an object counts as stale.",
            caution:
              "It changes which objects a finding names — not whether they are a problem.",
          },
        ],
      },
    ],
  },



  users: {
    title: "Operators",
    purpose: "Who may use this console, and with which role.",
    sections: [
      {
        title: "The two roles",
        terms: [
          {
            term: "reader",
            meaning:
              "Global read across every part of the console. A first sign-in is provisioned " +
              "as this.",
          },
          {
            term: "admin",
            meaning:
              "Additionally runs scans, accepts findings, records attestations, and manages " +
              "credentials, settings and operators.",
            caution:
              "There is no tier between them: anything that changes what this console reports " +
              "is an admin action.",
          },
        ],
      },
      {
        title: "What to know",
        body: [
          "The role lives in this product's own table and is never read from a token claim. " +
            "An invitation is a pending row until that person signs in for the first time; a " +
            "pending admin does not count towards the last-admin guard, because they cannot " +
            "yet sign in to use it.",
        ],
      },
    ],
  },

  audit: {
    title: "Audit log",
    purpose: "Every change anybody made through this console.",
    sections: [
      {
        title: "What is recorded",
        body: [
          "Every mutation: a setting changed, a risk accepted, an attestation recorded, a " +
            "credential rotated, an operator invited or revoked. Each row carries who, when " +
            "and what.",
          "It records what this CONSOLE did. It is not the tenant's own audit log, and it says " +
            "nothing about changes made in Microsoft's portals.",
        ],
      },
    ],
  },

  scan_detail: {
    title: "Scan run",
    purpose: "What one collection run managed to read, what it could not, and why.",
    sections: [
      {
        title: "The counters",
        body: [
          "Collectors total, done and failed are what the run itself recorded. A counter " +
            "the run never recorded is drawn hatched and reads as absent.",
        ],
        terms: [
          {
            term: "Progress",
            meaning:
              "Collectors done out of the number planned. A collector the capability probe " +
              "gated out was never planned work and is in neither number.",
            caution:
              "“Progress unknown” means the run recorded no total, not that nothing happened. " +
              "It is never shown as 0 %.",
          },
          {
            term: "Live / polling",
            meaning:
              "While the run is active the page follows it over a live stream. If the stream " +
              "is unavailable it polls every 5 seconds and says so.",
          },
          {
            term: "Cancel this scan",
            meaning:
              "Marks the run cancelled so the collectors stop at their next poll, and removes " +
              "the queued collector tasks. Whatever was already collected stays.",
            caution: "A cancelled run is reported as cancelled, never as complete.",
          },
        ],
      },
      {
        title: "Collector outcomes",
        body: [
          "One row per collector, from the capability snapshot this run used. A skipped " +
            "collector is drawn hatched and carries the reason and the service's own detail. " +
            "It is why some checks on the findings page are not collected rather than passing " +
            "or failing.",
        ],
        terms: [
          {
            term: "Skipped — denied",
            meaning: "The probe established that the credential lacks what the collector needs.",
          },
          {
            term: "Skipped — could not determine",
            meaning:
              "The probe itself could not run, so nothing is known either way. An unreachable " +
              "probe is not evidence.",
          },
          {
            term: "Granted but degraded",
            meaning:
              "The collector ran with a limitation it named — a beta-only field, a concealed " +
              "report — and the rows it produced are marked accordingly.",
          },
        ],
      },
      {
        title: "What to do",
        body: [
          "A run with skipped collectors points at a permission or a connector, not at the " +
            "tenant. Administration → Tenant connection → Capabilities names the missing capability and the " +
            "permission that would fix it — where a permission IS the fix.",
          "The findings link at the bottom opens the posture page pinned to this run, so what " +
            "you read there is what this run measured and nothing newer.",
        ],
      },
    ],
  },

  inventory_detail: {
    title: "Inventory object",
    purpose: "One object as the last scan found it, with where each value came from.",
    sections: [
      {
        title: "What you are looking at",
        body: [
          "The display name, kind and object id, then the attributes exactly as Microsoft's " +
            "API returned them. Nothing on this page is derived or estimated.",
          "A deleted object keeps its page. The name is struck through, the deletion date and " +
            "the reason the collector learned it are shown, and the attributes stay as they " +
            "were last read.",
        ],
      },
      {
        title: "Reading the sections",
        terms: [
          {
            term: "Provenance",
            meaning:
              "Which endpoint and API version the object was read from, and when. A property " +
              "that exists only in the beta endpoint says so here.",
          },
          {
            term: "Attributes",
            meaning:
              "The stored payload, one row per property. An absent property on a row that was " +
              "read is a measurement — Microsoft omits null values rather than sending them.",
            caution:
              "“No attribute payload is stored” means the collector kept only the identity of " +
              "this object. That is not an object with no attributes.",
          },
          {
            term: "Change history",
            meaning:
              "Every recorded change to this object, each tagged with how it was learned: from " +
              "Microsoft's delta stream, or by comparing two snapshots.",
          },
        ],
      },
      {
        title: "What to do",
        body: [
          "A value you expected but do not see is a version question before it is a " +
            "permission question: some properties exist only in the beta endpoint, and beta is " +
            "gated behind Administration → Settings → Preview.",
        ],
      },
    ],
  },

  check_detail: {
    title: "Finding",
    purpose: "One check's result for one run, with the evidence it was decided on.",
    sections: [
      {
        title: "What you are looking at",
        body: [
          "The same panel the findings table opens as a drawer, on its own page so it can be " +
            "pasted into a ticket. Without ?run= in the address it shows the latest run; with " +
            "it, exactly that run, however old.",
        ],
      },
      {
        title: "The sections",
        terms: [
          {
            term: "Provenance",
            meaning:
              "The endpoint or cmdlet the evidence came from, when it was collected, the data " +
              "age, the affected-object count, when the finding was first seen, and the " +
              "framework controls it maps to.",
            caution:
              "An absent affected count reads “not counted”, never 0: the collector did not " +
              "count, which is a different fact from counting zero.",
          },
          {
            term: "Evidence",
            meaning:
              "The stored payload the rule evaluated. It is purged on the retention schedule, " +
              "and the page says so rather than showing an empty box.",
          },
          {
            term: "Why this check could not run",
            meaning:
              "Shown instead of evidence when the result is not collected. It carries the " +
              "reason code and the service's own error text.",
          },
          NOT_COLLECTED,
          ACCEPTED,
        ],
      },
      {
        title: "What to do",
        body: [
          "Remediation describes the change to make in the tenant. This console makes none " +
            "of them: it holds no write permission.",
          "Accept as known risk records an owner, a reason and an expiry; the result stays " +
            "“fail” in the history. Attest records a manual answer for a control no rule can " +
            "evaluate. Both are admin actions and both appear in the audit log.",
        ],
      },
    ],
  },

  tenants_overview: {
    title: "Tenants",
    purpose: "Every tenant this deployment holds, side by side.",
    sections: [
      {
        title: "What you are looking at",
        body: [
          "One card per tenant, each showing that tenant's bucket scores (Critical & high and " +
            "Medium & low, each with its coverage) from its own last finished scan. Nothing here is averaged across tenants: an average would hide " +
            "the one tenant that is in trouble behind four that are not.",
          COVERAGE_RULE,
        ],
      },
      {
        title: "The states a row can be in",
        terms: [
          {
            term: "Never scanned",
            meaning:
              "The tenant exists and no scan has finished for it yet. It is drawn hatched, " +
              "like every other absence in this console.",
            caution:
              "It is not a score of zero. A tenant nobody has scanned has not failed " +
              "anything - nothing has been measured about it at all.",
          },
          {
            term: "Not measured",
            meaning:
              "A scan finished but produced no scored unit, so there is no percentage to " +
              "show. The coverage beside it says how little was evaluated.",
          },
          {
            term: "Not available",
            meaning:
              "The server could not read that figure for this tenant - the run store or the " +
              "credential store did not answer.",
            caution:
              "Different from \u201cnone\u201d and from \u201cnever\u201d: nobody established the value, " +
              "so no claim is made either way.",
          },
          {
            term: "Disabled",
            meaning:
              "The tenant is not scanned and no scan can be started for it. Everything " +
              "already collected stays readable.",
          },
        ],
      },
      {
        title: "What to do",
        body: [
          "Open a tenant to see its own console. Sorting puts the worst measured score " +
            "first and the unmeasured tenants last, because an unmeasured tenant is a " +
            "question rather than a result.",
        ],
      },
    ],
  },

  tenants: {
    title: "Manage tenants",
    purpose: "Add a tenant, rename one, or stop scanning one.",
    sections: [
      {
        title: "What a tenant is here",
        body: [
          "One customer directory, with its own Entra application credential, its own scans, " +
            "its own findings and its own settings. Nothing is shared between them except " +
            "the operators who sign in and the deployment-wide settings.",
          "The slug is the tenant's address - /t/<slug>/... - and it is permanent. The " +
            "display name is not: renaming a tenant leaves every existing link working.",
        ],
      },
      {
        title: "Enabling and disabling",
        terms: [
          {
            term: "Disable",
            meaning:
              "Stops scheduled scans and refuses new ones for that tenant. Its collected " +
              "data, findings and audit trail stay exactly as they are and stay readable.",
          },
          {
            term: "The default tenant",
            meaning:
              "The one every address from before tenants existed resolves to, and the one " +
              "the unprefixed API serves. It cannot be disabled.",
          },
        ],
      },
      {
        title: "What is deliberately absent",
        body: [
          "There is no delete. A tenant carries the record that its scans, its accepted " +
            "risks and its operator actions happened, and removing it would remove that " +
            "record. Disabling stops the work and keeps the history.",
          "A new tenant has no credential, so nothing is collected for it until one is " +
            "registered on its own Entra application settings page.",
        ],
      },
    ],
  },

  platform_settings: {
    title: "Platform settings",
    purpose: "The settings that apply to the whole deployment rather than to one tenant.",
    sections: [
      {
        title: "Why these are separate",
        body: [
          "A tenant setting changes what this console reports about one customer directory. " +
            "A platform setting changes how the deployment behaves for every tenant at " +
            "once - when the cleanup runs, how long the audit trail is kept, how many " +
            "tenants may be scanned at a time, and whether this deployment holds more than " +
            "one tenant at all.",
          "They are stored differently too, so the server refuses a platform key sent " +
            "through a tenant's settings page rather than writing it somewhere nothing " +
            "would read it.",
        ],
      },
      {
        title: "The multitenant switch",
        terms: [
          {
            term: "Off",
            meaning:
              "This deployment serves exactly one tenant - the default one. Only it is " +
              "scanned, scheduled and thinned, and every legacy address resolves to it.",
          },
          {
            term: "On",
            meaning:
              "Every enabled tenant is scanned on its own schedule and appears in the " +
              "switcher. No restart is needed: the change takes hold on the next minute " +
              "tick.",
          },
        ],
      },
    ],
  },
};

/** The page ids, for the guard test. */
export const HELP_PAGE_IDS = Object.keys(PAGE_HELP);
